// What the phone keeps to send once it's online (the visits logged with no signal), in the
// browser's own database so photos fit and it outlives a closed app. Each item is sent in
// turn; one the server took (or turned down for good) leaves the box.

const DB = "clientos-outbox";
const STORE = "items";

export type OutboxItem = { id: string; path: string; body: Record<string, unknown>; label: string; error?: string };

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "id" });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function run<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const req = fn(db.transaction(STORE, mode).objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const listeners = new Set<() => void>();
const changed = () => listeners.forEach((l) => l());

/** Calls back whenever the box changes; returns how to stop. */
export function onOutbox(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export const outboxItems = () => run<OutboxItem[]>("readonly", (s) => s.getAll() as IDBRequest<OutboxItem[]>);

export async function addToOutbox(item: OutboxItem) {
  await run("readwrite", (s) => s.put(item));
  changed();
}

let sending: Promise<number> | null = null;

/**
 * Sends what's waiting, in turn, with post (the API client's): returns how many went. One the
 * server turns down for good (4xx other than a timeout or too many) is kept with its error, to
 * be seen; a network failure stops the round until the next.
 */
export function flushOutbox(post: (path: string, body: unknown) => Promise<unknown>): Promise<number> {
  sending ??= (async () => {
    let sent = 0;
    try {
      for (const item of await outboxItems()) {
        if (item.error) continue;
        try {
          await post(item.path, item.body);
          await run("readwrite", (s) => s.delete(item.id));
          sent++;
        } catch (e) {
          const status = (e as { status?: number }).status;
          if (!status || status === 408 || status === 429 || status >= 500) break;
          await run("readwrite", (s) => s.put({ ...item, error: (e as Error).message || String(status) }));
        }
        changed();
      }
    } finally {
      sending = null;
    }
    return sent;
  })();
  return sending;
}

export async function dropFromOutbox(id: string) {
  await run("readwrite", (s) => s.delete(id));
  changed();
}
